import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NotificationsService, ProjectService } from '@catcore/services';
import { Project } from '@catcore/types/api-v2';
import { FileBlob } from '@catcore/types/base';
import { UserdataConfigService } from '@catmodels/userdata-generation/services/userdata-config.service';
import { AircraftRegistrationService } from '@catmodules/models/userdata-generation/services/aircraft-registration.service';
import { UserdataFileService } from '@catmodules/models/userdata-generation/services/userdata-file.service';
import {
  BriefingSelection,
  UploadTypes,
  UserdataConfig,
  UserdataFile,
  UserdataImages,
} from '@catmodules/models/userdata-generation/types';
import * as FileSaver from 'file-saver';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import {UserdataFileVersionService} from "@catmodels/userdata-generation/services/userdata-file-version.service";

const AIRCRAFT_REGISTRATION_ALL = 'ALL';
const FILE_TYPE_SCRIPT = 'USERDATA_SCRIPT';
const FILE_TYPE_FAREWELL = 'FAREWELL';
const FILE_TYPE_WELCOME = 'WELCOME';
const FILE_TYPE_LOGO = 'LOGO';
const FILE_TYPE_AUDIO_TEST = 'AUDIO_TEST';
const FILE_TYPE_AUDIO_BRIEFING = 'AUDIO_BRIEFING';

@Component({
  selector: 'catapp-userdata-generation-briefings',
  templateUrl: './userdata-generation-briefings.component.html',
  styleUrls: ['./userdata-generation-briefings.component.scss'],
})
export class UserdataGenerationBriefingsComponent implements OnInit, OnDestroy {

  // Form
  form: FormGroup;
  registrationOptions: any;
  fileVersionOptions: any;

  // Userdata configuration
  userdataConfig: UserdataConfig;
  configLoaded: Subject<boolean> = new Subject();

  // Briefings
  briefings: UserdataFile[];
  briefingsSelection: BriefingSelection[] = new Array<BriefingSelection>();

  // Default data
  projectNumber = 'xxx';
  formattedVersion = 'yy';
  defaultContent: UserdataFile[];
  userdataFiles: UserdataFile[];
  defaultImages = new UserdataImages();

  // Images
  logoFile: SafeUrl;
  farewellFile: SafeUrl;
  welcomeFile: SafeUrl;

  // Final package
  packageName = 'source';
  briefingsFolder = [];
  userDataFolder = [];
  imageFolder = [];
  zip_existing = false;

  // Subscribtions
  private _aircraftRegistrationSubscription: Subscription;
  private _projectSubscription: Subscription;
  private _userdataFileSubscription: Subscription;
  private _userdataFileVersionSubscription: Subscription;
  private _userdataConfigSubscription: Subscription;

  /**
   * Creates an instance of userdata generation briefings component.
   * @param sanitizer
   * @param aircraftRegistrationService
   * @param projectService
   * @param userdataFileService
   * @param userdataFileVersionService
   * @param notificationService
   * @param userdataConfigService
   */
  constructor(
    private sanitizer: DomSanitizer,
    private aircraftRegistrationService: AircraftRegistrationService,
    private projectService: ProjectService,
    private userdataFileService: UserdataFileService,
    private userdataFileVersionService: UserdataFileVersionService,
    private notificationService: NotificationsService,
    private userdataConfigService: UserdataConfigService) {
    this.form = new FormGroup({
      id: new FormControl(null, []),
      fileVersion: new FormControl(null, []),
      registration: new FormControl(null, []),
      smoking: new FormControl(false, []),
      bunkbed: new FormControl(false, []),
      extended: new FormControl(false, []),
      version: new FormControl(false, []),
      logo: new FormControl('NONE', []),
      logo_file: new FormControl(null, []),
      logo_file_name: new FormControl(null, []),
      welcome: new FormControl('NONE', []),
      welcome_file: new FormControl(null, []),
      welcome_file_name: new FormControl(null, []),
      farewell: new FormControl('NONE', []),
      farewell_file: new FormControl(null, []),
      farewell_file_name: new FormControl(null, []),
    });
  }

  /**
   * on init
   */
  ngOnInit() {
    this._projectSubscription = this.projectService
      .getCurrentProject()
      .delay(100)
      .subscribe((project: Project) => {
        this.projectNumber = String(project.number);
        if (project.id) {
          this._userdataConfigSubscription = this.userdataConfigService.getProjectConfig(String(project.id)).subscribe((config: UserdataConfig) => {
            this.userdataConfig = config;
            this.logoFile = this.sanitizer.bypassSecurityTrustUrl(config.logo_file);
            this.welcomeFile = this.sanitizer.bypassSecurityTrustUrl(config.welcome_file);
            this.farewellFile = this.sanitizer.bypassSecurityTrustUrl(config.farewell_file);
            this.loadUserdataConfig();
          });
        }
        // Add leading zeros to project number
        while (this.projectNumber.length < 3) {
          this.projectNumber = '0' + this.projectNumber;
        }
      });
    this._aircraftRegistrationSubscription = this.aircraftRegistrationService.getAll()
      .subscribe((registrations) => {
        this.registrationOptions = registrations
          .filter((data) => data.name !== AIRCRAFT_REGISTRATION_ALL)
          .map((registration) => ({ value: registration._links.self.href, label: registration.name }));
        this.loadUserdataConfig();
      });

    this._userdataFileVersionSubscription = this.userdataFileVersionService.getAll()
      .subscribe((fileVersions) => {
        this.fileVersionOptions = fileVersions
          .map((fileVersion) => ({ value: fileVersion._links.self.href, label: fileVersion.name }));
        this.loadUserdataConfig();
      });

    this._userdataFileSubscription = this.userdataFileService.getAll().subscribe((files) => {
      this.briefings = files
        .filter((file) => file._links.type.label === FILE_TYPE_AUDIO_BRIEFING)
        .sort((a, b) => (a.filename > b.filename) ? 1 : ((b.filename > a.filename) ? -1 : 0));
      this.defaultImages.farewell = files.filter((file) => file._links.type.label === FILE_TYPE_FAREWELL)[0];
      this.defaultImages.logo = files.filter((file) => file._links.type.label === FILE_TYPE_LOGO)[0];
      this.defaultImages.welcome = files.filter((file) => file._links.type.label === FILE_TYPE_WELCOME)[0];
      this.defaultContent = files.filter((file) => (file._links.type.label === FILE_TYPE_SCRIPT ||
        file._links.type.label === FILE_TYPE_AUDIO_TEST));
      this.loadUserdataConfig();
    });
  }

  /**
   * on destroy
   */
  ngOnDestroy() {
    if (this._userdataFileSubscription) {
      this._userdataFileSubscription.unsubscribe();
    }
    if (this._projectSubscription) {
      this._projectSubscription.unsubscribe();
    }
    if (this._aircraftRegistrationSubscription) {
      this._aircraftRegistrationSubscription.unsubscribe();
    }
    if (this._userdataConfigSubscription) {
      this._userdataConfigSubscription.unsubscribe();
    }
    if (this._userdataFileVersionSubscription) {
      this._userdataFileVersionSubscription.unsubscribe();
    }
  }

  /**
   * Recreates the structure of the userdata folder when a changes has been detected in the form.
   */
  updateBriefingSelection() {
    // empty the briefing selection
    this.briefingsSelection.length = 0;
    this.briefingsFolder.length = 0;
    // when the registration is not selected, no briefing can be added
    if (this.form.controls['registration'].value !== ''
        && this.form.controls['fileVersion'].value !== '') {
      this.resetPackageName();
      this.briefings
        .filter((briefing) => briefing._links.version.href === this.form.controls['fileVersion'].value)
        .forEach((briefing) => {
        // add only briefings related to the selected registration
        if (
          briefing._links.registration.label === AIRCRAFT_REGISTRATION_ALL ||
          this.form.controls['registration'].value ===
          briefing._links.registration.href
        ) {
          const selected = this.selectBriefing(briefing);
          this.briefingsSelection.push({ briefing, selected } as BriefingSelection);
          if (selected) {
            this.briefingsFolder.push(`${this.packageName}${briefing.target_package_path}/${briefing.target_package_filename}`);
          } else {
            this.briefingsFolder.push(`${this.packageName}${briefing.target_package_path}/`);
          }
        }
      });
    }
    this.createScreensFolder();
    this.createUserDataFolder();
  }

  /**
   * Loads userdata config
   */
  loadUserdataConfig() {
    if (this.userdataConfig) {
      this.form.patchValue(this.userdataConfig);
      this.form.patchValue({ id: this.userdataConfig.id });
      if (this.userdataConfig._links.registration) {
        this.form.patchValue({ registration: this.userdataConfig._links.registration.href });
      } else {
        this.form.patchValue({ registration: this.registrationOptions[1].value });
      }
      if (this.userdataConfig._links.file_version) {
        this.form.patchValue({ fileVersion: this.userdataConfig._links.file_version.href });
      } else {
        this.form.patchValue({ fileVersion: this.fileVersionOptions[0].value });
      }
      this.form.patchValue({ version: this.userdataConfig.version });
      this.formattedVersion = this.userdataConfig.formatted_version;
      this.zip_existing = this.userdataConfig.zip_existing;
      this.form.patchValue({ logo: this.userdataConfig.logo });
      this.form.patchValue({ welcome: this.userdataConfig.welcome });
      this.form.patchValue({ farewell: this.userdataConfig.farewell });
      this.configLoaded.next(true);
      this.updateBriefingSelection();
    }
  }

  /**
   * Int version to str
   * @param version
   * @returns string
   */
  private intVersionToStr(version: number) {
    if (version <= 9) {
      return '0' + version.toString();
    }
    if (version <= 99) {
      return version.toString();
    }
    const ch1 = (Math.trunc(((version - 100) / 36)) + 10).toString(36);
    const ch2 = ((version - 100) % 36).toString(36);
    return (ch1 + ch2).toUpperCase();
  }

  /**
   * Define wether a briefing shall be part or not of the package based on the user selection.
   *
   * @param {UserdataFile} briefing Briefing to be selected
   * @returns {boolean} true if selected
   */
  selectBriefing(briefing: UserdataFile) {
    const bunkbedSelected = this.form.controls['bunkbed'].value;
    const smokingSelected = this.form.controls['smoking'].value;
    const extendedSelected = this.form.controls['extended'].value;

    if (briefing.baseline === false) {
      return (extendedSelected && (!briefing.smoking || smokingSelected) && (!briefing.bunkbed || bunkbedSelected));
    }
    // Smoking and bunkbed briefings are interdepent. A briefing needs to satisfy both requirement to be included
    return ((briefing.smoking === smokingSelected && briefing.bunkbed === bunkbedSelected) ||
      // Briefings always part of package shall always be selected.
      (briefing.always_part_of_package));
  }

  /**
   * Creates screens folder
   */
  createScreensFolder() {
    const logoDropdown = this.form.controls['logo'].value;
    const logoUpload = this.form.controls['logo_file'].value;
    const welcomeDropdown = this.form.controls['welcome'].value;
    const welcomeUpload = this.form.controls['welcome_file'].value;
    const farewellDropdown = this.form.controls['farewell'].value;
    const farewellUpload = this.form.controls['farewell_file'].value;

    this.imageFolder.length = 0;
    if (logoDropdown === UploadTypes.Default || logoDropdown === UploadTypes.Upload && logoUpload != null) {
      this.imageFolder.push(`${this.defaultImages.logo.target_package_path}/${this.defaultImages.logo.target_package_filename}`);
    }
    if (welcomeDropdown === UploadTypes.Default || welcomeDropdown === UploadTypes.Upload && welcomeUpload != null) {
      this.imageFolder.push(`${this.defaultImages.welcome.target_package_path}/${this.defaultImages.welcome.target_package_filename}`);
    }
    if (farewellDropdown === UploadTypes.Default || farewellDropdown === UploadTypes.Upload && farewellUpload != null) {
      this.imageFolder.push(`${this.defaultImages.farewell.target_package_path}/${this.defaultImages.farewell.target_package_filename}`);
    }
    if (this.imageFolder.length === 0) {
      this.imageFolder.push(`${this.defaultImages.farewell.target_package_path}`);
    }
  }

  /**
   * Merges all the component of the folder to create a final package definition.
   */
  createUserDataFolder() {
    this.userDataFolder = [
      ...this.defaultContent.map((content) => (`${this.packageName}${content.target_package_path}/${content.target_package_filename}`)),
      ...this.imageFolder.map((content) => (`${this.packageName}${content}`)),
      ...this.briefingsFolder];
  }

  /**
   * Recomputes the name of the package, it is dependent on the selection of the user.
   * @example
   * - C-xxx-USRE1-yy for baseline EASA briefings.
   * - C-xxx-USRE2-yy for extended EASA briefings.
   * - C-xxx-USRF1-yy for baseline FAA briefings.
   * - C-xxx-USRF2-yy for extended FAA briefings.
   * xxx is the project number and yy is the build count.
   */
  resetPackageName() {
    const extended = this.form.controls['extended'].value ? 2 : 1;
    let registration: string;
    if (this.registrationOptions) {
      this.registrationOptions.forEach((reg) => {
        if (reg.value === this.form.controls['registration'].value) {
          registration = reg.label[0];
        }
      });
    }
    this.packageName = `C-${this.projectNumber}-USR${registration}${extended}-${this.formattedVersion}`;
  }

  formatVersion() {
    const version = this.form.controls['version'].value;
    this.formattedVersion = version ? this.intVersionToStr(version) : 'yy';
    this.updateBriefingSelection();
  }

  /**
   * Determines whether download zip on
   */
  onDownloadZip() {
    const subscription = this.userdataConfigService
      .downloadSources(this.userdataConfig)
      .finally(() => subscription.unsubscribe())
      .subscribe((fileBlob: FileBlob) => {
        FileSaver.saveAs(fileBlob.blob, `${this.packageName}_defaultuserdata.zip`);
      });
  }

  checkFormCompletion(): boolean {
    let result = true;

    if (this.form.get('registration').value === null) {
      this.notificationService.error('Missing parameter : Registration', 'No aircraft registration selected');
      result = false;
    }
    if (this.form.get('fileVersion').value === null) {
      this.notificationService.error('Missing parameter : File Version', 'No file version selected');
      result = false;
    }
    if (this.form.get('logo_file').value === null && this.form.get('logo').value === UploadTypes.Upload) {
      this.notificationService.error('Missing parameter : Logo', 'No logo image uploaded');
      result = false;
    }
    if (this.form.get('welcome_file').value === null && this.form.get('welcome').value === UploadTypes.Upload) {
      this.notificationService.error('Missing parameter : Welcome image', 'No welcome image uploaded');
      result = false;
    }
    if (this.form.get('farewell_file').value === null && this.form.get('farewell').value === UploadTypes.Upload) {
      this.notificationService.error('Missing parameter : Farewell image', 'No farewell image uploaded');
      result = false;
    }
    if (this.form.get('version').value === null) {
      this.notificationService.error('Missing parameter : Package version', 'Please enter package version');
      result = false;
    } else if (this.form.get('version').value <= 0) {
      this.notificationService.error('Error on parameter : Package version', 'The package version number can not be below 0');
      result = false;
    } else if (this.form.get('version').value > 1035) {
      this.notificationService.error('Error on parameter : Package version', 'The package version number can not be above 1035');
      result = false;
    }
    if (this.form.get('version').pristine) {
      this.notificationService.warn('Version number not edited', 'This can lead to unexpected results');
    }
    return result;
  }

  /**
   * Determines whether submit on
   */
  onSubmit() {
    if (!this.checkFormCompletion()) {
      return;
    }
    const formValue = this.form.value;
    if (this.form.get('logo_file').pristine) {
      delete formValue['logo_file'];
      delete formValue['logo_file_name'];
    }
    if (this.form.get('farewell_file').pristine) {
      delete formValue['farewell_file'];
      delete formValue['farewell_file_name'];
    }
    if (this.form.get('welcome_file').pristine) {
      delete formValue['welcome_file'];
      delete formValue['welcome_file_name'];
    }
    const subscription = this.userdataConfigService.update(this.form.value)
      .finally(() => subscription.unsubscribe())
      .subscribe(() => {
        const subscriptionCreatePackage = this.userdataConfigService.createPackage(this.userdataConfig)
          .finally(() => subscriptionCreatePackage.unsubscribe())
          .subscribe(() => {
            this.notificationService.success('Userdata content creation successful', 'The userdata content has been stored as an uploaded file (zip format) in the current project.');
            this.form.markAsPristine();
            this.userdataConfig.zip_existing = true;
            this.zip_existing = true;
          });
      });
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/models/userdata-generation/components/userdata-generation-briefings/userdata-generation-briefings.component.ts